"use strict"

import React from "react"
import ReactDOM from "react-dom"
import { select } from "d3-selection"
import { FormattedMessage } from "react-intl"

import ErrorBoundary, { logToServer } from "./ErrorBoundary"
import FermManagement from "./components/fermmanagement/FermManagement"
import FermManagementRun from "./components/fermmanagement/FermManagementRun"
import SourceRegistrationDash from "./components/sourceregistration/SourceRegistrationDash"
import SampleManagementDash from "./components/samplemanagement/SampleManagementDash"
import InventoryDash from "./components/inventorymanagement/InventoryDash"
import LTPMDashboard from "./components/graph_dashboards/LTPMDashboard"
import ArchivedDashboard from "./components/graph_dashboards/ArchivedDashboard"
import DataTransferScreen from "./components/datatransfer/DataTransferScreen"
import DatabaseBackupTable from "./components/administration/database_backup/DatabaseBackupTable"
import SystemManagementScreen from "./components/administration/system_management/SystemManagementScreen"
import UsersTable from "./components/administration/user_management/UsersTable"
import FileUploadScreen from "./components/administration/file_uploads/FileUploadScreen"

import { getObjectsList } from "./Loaders"
import GraphConfigProvider from "./shared_components/config/GraphConfigProvider"
import FermManagementConfigProvider from "./config/FermManagementConfigProvider"
import SourceRegistrationConfigProvider from "./config/SourceRegistrationConfigProvider"
import AppSettingsConfigProvider, { PageWrapper } from "./config/AppSettingsConfigProvider"
import PlotSettingsConfigProvider from "./shared_components/config/PlotSettingsConfigProvider"
import FontSizeConfigProvider from "./shared_components/config/FontSizeConfigProvider"
import CrossHairConfigProvider from "./shared_components/config/CrossHairConfigProvider"
import FavouriteConfigProvider from "./shared_components/config/FavouriteConfigProvider"
import InventoryConfigProvider from "./config/InventoryConfigProvider"
import SampleConfigProvider from "./config/SampleConfigProvider"
import ZoomConfigProvider from "./shared_components/config/ZoomConfigProvider"
import OperatorActionConfigProvider from "./config/OperatorActionConfigProvider"
import UserFiles from "./components/administration/user_files/UserFiles"

function ApplicationWrapper({ children }) {
  // General wrapper for each page of the application to inherit from
  // Contains the ErrorBoundary and any configurations that are general to the application from
  // AppSettingsConfigProvider
  return (
    <ErrorBoundary>
      <AppSettingsConfigProvider>
        <OperatorActionConfigProvider>{children}</OperatorActionConfigProvider>
      </AppSettingsConfigProvider>
    </ErrorBoundary>
  )
}

function GraphPageWrapper({ children, timestampDefault = false, graphInterface = null }) {
  // General wrapper for pages that use the main application graphing machinery
  return (
    <ApplicationWrapper>
      <CrossHairConfigProvider>
        <FavouriteConfigProvider>
          <ZoomConfigProvider>
            <PlotSettingsConfigProvider timestampDefault={timestampDefault}>
              <FontSizeConfigProvider>
                <GraphConfigProvider graphInterface={graphInterface}>
                  <PageWrapper>{children}</PageWrapper>
                </GraphConfigProvider>
              </FontSizeConfigProvider>
            </PlotSettingsConfigProvider>
          </ZoomConfigProvider>
        </FavouriteConfigProvider>
      </CrossHairConfigProvider>
    </ApplicationWrapper>
  )
}

function live_dashboard(
  containerID,
  graphInterface,
  cutoff,
  defaultSelectPlaceholders,
  baseUrl,
  srcType,
  appConfig,
) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }
  const LIVE_DASH_SELECTIVE_LOADING = (appConfig?.settings || {})?.LIVE_DASH_SELECTIVE_LOADING || false
  ReactDOM.render(
    <GraphPageWrapper graphInterface={graphInterface} timestampDefault={true}>
      <LTPMDashboard
        queryParams={{ cutoff: cutoff }}
        doFilterDefaultRuns={(arr) =>
          arr.filter(
            (r) =>
              r.type === srcType &&
              (defaultSelectPlaceholders ? true : r.placeholder === defaultSelectPlaceholders),
          )
        }
        baseRunidUrl={baseUrl}
        LIVE_DASH_SELECTIVE_LOADING={LIVE_DASH_SELECTIVE_LOADING}
      />
    </GraphPageWrapper>,
    container,
  )
}

function archived_dashboard(
  containerID,
  selections,
  updateRunList,
  onSearch,
  selectedRun,
  updateSelectedRun,
) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <GraphPageWrapper graphInterface={"Performance Analysis"} timestampDefault={true}>
      <ArchivedDashboard
        runs={selections}
        updateRunList={updateRunList}
        onSearch={onSearch}
        selectedRun={selectedRun}
        updateSelectedRun={updateSelectedRun}
      />
    </GraphPageWrapper>,
    container,
  )
}

function ferm_management(containerID) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  const runName = containerID === "ferm-management-run" ? container.dataset?.runName || "" : ""

  ReactDOM.render(
    <ApplicationWrapper>
      <FermManagementConfigProvider>
        <PageWrapper>
          {containerID === "ferm-management-run" ? (
            <FermManagementRun runName={runName} />
          ) : (
            <FermManagement />
          )}
        </PageWrapper>
      </FermManagementConfigProvider>
    </ApplicationWrapper>,
    container,
  )
}

function source_registration(containerID) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <SourceRegistrationConfigProvider>
        <PageWrapper>
          <SourceRegistrationDash />
        </PageWrapper>
      </SourceRegistrationConfigProvider>
    </ApplicationWrapper>,
    container,
  )
}

function sample_management(containerID, isNSView = false) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <SampleConfigProvider isNSView={isNSView}>
        <PageWrapper>
          <SampleManagementDash />
        </PageWrapper>
      </SampleConfigProvider>
    </ApplicationWrapper>,
    container,
  )
}

function inventory_management(containerID) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <InventoryConfigProvider>
        <PageWrapper>
          <InventoryDash />
        </PageWrapper>
      </InventoryConfigProvider>
    </ApplicationWrapper>,
    container,
  )
}

function database_backup(containerID) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <PageWrapper>
        <DatabaseBackupTable />
      </PageWrapper>
    </ApplicationWrapper>,
    container,
  )
}

function system_management(containerID) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <PageWrapper>
        <SystemManagementScreen />
      </PageWrapper>
    </ApplicationWrapper>,
    container,
  )
}

function user_management(containerID) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <PageWrapper>
        <UsersTable />
      </PageWrapper>
    </ApplicationWrapper>,
    container,
  )
}

function user_files(containerID) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <PageWrapper>
        <UserFiles />
      </PageWrapper>
    </ApplicationWrapper>,
    container,
  )
}

function file_uploads(
  containerID,
  selections,
  selected_file_id,
  filterEditorOnSearch,
  tableReload,
  setFileSelection,
) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <PageWrapper>
        <FileUploadScreen
          selections={selections}
          selected_file_id={selected_file_id}
          setFileSelection={setFileSelection}
          filterEditorOnSearch={filterEditorOnSearch}
          tableReload={tableReload}
        />
      </PageWrapper>
    </ApplicationWrapper>,
    container,
  )
}

function permission_denied(containerID) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <PageWrapper>
        <div className={"full-height"}>
          <div className={"content-block full-height-min"}>
            <h2>
              <FormattedMessage id={"general.permission.denied"} description={"Permission Denied"} />
            </h2>
            <p>
              <FormattedMessage
                id={"general.permission.denied.message"}
                description={"You do not have permission to access this page"}
              />
            </p>
          </div>
        </div>
      </PageWrapper>
    </ApplicationWrapper>,
    container,
  )
}

function data_transfer(containerID) {
  const container = document.getElementById(containerID)
  if (!container) {
    throw Error(`Couldn't find container element ${containerID}`)
  }

  ReactDOM.render(
    <ApplicationWrapper>
      <PageWrapper>
        <DataTransferScreen />
      </PageWrapper>
    </ApplicationWrapper>,
    container,
  )
}

export function updateSourceRegistrationWarning() {
  /* Set background of Source Registration Nav Option on user FYI warning */
  getObjectsList("/warehouse/dcs_tables/has_warning/")
    .then((resp) => {
      if (resp["has_warning"]) {
        select("#source-reg-nav-option").attr("class", "navigation-list reactor_warning")
      } else {
        select("#source-reg-nav-option").attr("class", "navigation-list reactor_okay")
      }
    })
    .catch((err) => {
      console.error(err)
      logToServer(err)
    })
}

export function setPageDefaultLanguageAndRender(view, navID = null, andThen = false, base_nav_option = true) {
  /* Set background of currently selected nav option */
  if (navID && base_nav_option) {
    select(navID).attr("class", "navigation-list active")
  }

  updateSourceRegistrationWarning()

  /* Set Page Language */
  getObjectsList("/backend/jsconfig/")
    .then((config) => {
      let locale
      const browser_language = navigator && navigator.language ? navigator.language.split(/[-_]/)[0] : null
      if (config.user && config.user.language_preference) {
        locale = config.user.language_preference
      } else if (browser_language && config.locales_supported.includes(browser_language)) {
        locale = browser_language
      } else {
        locale = config.default_locale
      }
      window.lanzacofee.language = locale
      view(config)
      andThen && andThen(config)
    })
    .catch((err) => {
      view()
      andThen && andThen({})
      console.error(err)
      logToServer(err)
    })
}

/* Export to global namespace */
window.lanzacofee = {
  live_dashboard,
  archived_dashboard,
  ferm_management,
  source_registration,
  sample_management,
  inventory_management,
  data_transfer,
  database_backup,
  system_management,
  user_management,
  user_files,
  file_uploads,
  permission_denied,
}
